import { useRef, useState } from "react";
import Uploadimg from "../../assets/img/uploaddocoment.png";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import * as pdfjsLib from "pdfjs-dist";
import { toast } from "../../helper/common";

const allowedTypes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];

const UploadDocumentBox = ({
  _t,
  title,
  files,
  setFiles,
  loading,
  setGlobalInvalidFiles,
}) => {
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState([]);

  // 📁 Trigger file input manually
  const handleClick = () => {
    if (files.length === 0 && invalidFiles.length === 0) {
      fileInputRef.current.click();
    }
  };

  // ✅ Validate new files
  const validateFiles = async (newFiles) => {
    const validated = [];
    const invalids = [];

    for (let file of newFiles) {
      // Type check
      if (!allowedTypes.includes(file.type)) {
        invalids.push({
          id: `${file.name}-${file.lastModified}`,
          name: file.name,
          reason: _t("fileTypeNotAllowed"),
        });
        continue;
      }

      // Size check (max 4MB)
      if (file.size > 4 * 1024 * 1024) {
        invalids.push({
          id: `${file.name}-${file.lastModified}`,
          name: file.name,
          reason: _t("fileUploadedExceedsTwoPointFiveMbFileLimit"),
        });
        continue;
      }

      try {
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } else {
          // Validate image
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Corrupted image"));
            img.src = URL.createObjectURL(file);
          });
        }
        validated.push(file);
      } catch (err) {
        let reason = _t("isCorruptedOrUnreadableFile");
        if (err?.message?.toLowerCase()?.includes("password")) {
          reason = _t("isPasswordProtectedPDF");
        }
        invalids.push({
          id: `${file.name}-${file.lastModified}`,
          name: file.name,
          reason,
        });
      }
    }

    if (invalids.length > 0) {
      setGlobalInvalidFiles((prev) => [...prev, ...invalids]);
      setInvalidFiles((prev) => [...prev, ...invalids]);
    }
    return validated;
  };

  // 📦 File selection handler
  const handleFileChange = async (e) => {
    const selected = e.target.files;
    if (!selected?.length) return;

    const validFiles = await validateFiles(Array.from(selected));
    if (validFiles.length === 0) return;

    // total size check
    const totalSize =
      [...files, ...validFiles].reduce((sum, f) => sum + f.size, 0) /
      (1024 * 1024);
    if (totalSize > 20) {
      toast.error(_t("totalFileSizeExceeds20MB"));
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  // 📥 Drag-drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0)
      handleFileChange({ target: { files: dropped } });
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  // 🗑️ Remove handlers
  const removeValidFile = (id) =>
    setFiles((prev) => prev.filter((f) => f.id !== id));

  const removeInvalidFile = (id) => {
    setGlobalInvalidFiles((prev) => prev.filter((f) => f.id !== id));
    setInvalidFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // 📏 File size helper
  const formatSize = (size) => {
    const kb = size / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="uploadBoxContainer">
      <h2 className="uploadBoxHeading">{title}</h2>

      {loading ? (
        <div
          className="doc-listing doc-listing2 d-flex align-items-center justify-content-center text-center"
          style={{ height: 244 }}
        >
          <img
            src={SkorDefaultLoader}
            className="defaultSkorLoader"
            alt="loader"
          />
        </div>
      ) : (
        <>
          {/* File area */}
          <div
            className={`doc-listing doc-listing2 doc-listing3 h-100 d-flex align-items-center justify-content-center text-center ${
              files.length === 0 && invalidFiles.length === 0
                ? "cursor-pointer"
                : ""
            }`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            style={{
              backgroundColor: isDragging ? "#A3EB64" : "transparent",
              transition: "background-color 0.2s ease",
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept="application/pdf, image/jpeg, image/png, image/jpg"
              multiple
            />

            {files.length > 0 || invalidFiles.length > 0 ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "column",
                }}
              >
                <div className="uploaded-list">
                  {/* Invalid files */}
                  {invalidFiles.map((file) => (
                    <div
                      key={file.id}
                      style={{
                        backgroundColor: "#fdf5f5",
                        borderColor: "#f19492",
                      }}
                      className="d-flex uploaded-files-view align-items-center justify-content-between flex-wrap"
                    >
                      <div className="uploaded-files-view-name">
                        <img src="/images/docIconGreen.svg" alt="" />
                        <div className="upload-file-name">
                          <h1
                            className="mb-0 text-truncate"
                            style={{ maxWidth: "250px" }}
                          >
                            {file.name}
                          </h1>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between ">
                        <div
                          style={{ color: "#dc2828" }}
                          className="corrupted-upload-files"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-circle-alert w-5 h-5 text-destructive"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" x2="12" y1="8" y2="12"></line>
                            <line x1="12" x2="12.01" y1="16" y2="16"></line>
                          </svg>
                          <span>{file.reason}</span>
                        </div>
                        <div
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeInvalidFile(file.id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-trash2 w-4 h-4"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Valid files */}
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="d-flex uploaded-files-view align-items-center justify-content-between file-item flex-wrap"
                    >
                      <div className="uploaded-files-view-name">
                        <img src="/images/docIconGreen.svg" alt="" />
                        <div className="upload-file-name">
                          <h1
                            className="text-truncate"
                            style={{ maxWidth: "250px" }}
                          >
                            {file.name}
                          </h1>
                          <span>{formatSize(file.size)}</span>
                        </div>
                      </div>
                      <div
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeValidFile(file.id);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-trash2 w-4 h-4"
                        >
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add more files */}
                <div className="text-center">
                  <button
                    type="button"
                    className="btnNext btnSelectFile selectFilehere"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current.click();
                    }}
                  >
                    {_t("addMoreFiles")}
                  </button>
                </div>
              </div>
            ) : (
              // Default upload UI
              <div className="docUpload">
                <div className="Uploadimgdiv">
                  <img src={Uploadimg} alt="upload-img" />
                </div>
                <p className="drag-drop-content pt-3">
                  {_t("dragAndDropYourDocumentsHere")}
                </p>
                <p className="spacepx">{_t("or")}</p>
                <button
                  type="button"
                  className="btnNext btnSelectFile selectFilehere"
                >
                  {_t("selectFromFiles")}
                </button>
                <p className="fileSizeText">
                  {_t(
                    "ensureTheSizeOfFileMustBeLessThan2MbOr20MbForBulkFileUpload"
                  )}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UploadDocumentBox;
